import {
  BaseQueryApi,
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import {OtsAsyncThunkConfigExtra, OtsState} from "./ots-store";
import {removeUndefined} from "../util/util";

export const otsFetch = (authToken: string, input: RequestInfo | URL, init?: RequestInit) => (): Promise<Response> => {
  const initOptions: RequestInit = {
    ...(init ?? {})
  }

  const options = {
    ...initOptions,
    headers: {
      ...initOptions.headers,
      Authorization: authToken
    }
  }

  return window.fetch(input, options)
}

type QueryApi = Pick<BaseQueryApi, "type" | "getState" | "extra" | "endpoint" | "forced">

export const buildStandardHeaders = (baseQueryApi: QueryApi): Record<string, any> => {
  const extra = baseQueryApi.extra as OtsAsyncThunkConfigExtra;
  const authToken = extra.getAuthToken(baseQueryApi.getState() as OtsState);

  return removeUndefined({
    'Authorization': authToken,
    'X-Tenant-Id': extra.tenantCode,
    'X-Client-App': extra.clientApp,
    'X-Client-Version': import.meta.env.VITE_APP_VERSION,
    'X-Client-Platform': 'web',
  })
}

const otsFetchBaseQuery = fetchBaseQuery({
  baseUrl: '',
  prepareHeaders: (headers, api) => {
    const standardHeaders = buildStandardHeaders(api);

    for (const [key, value] of Object.entries(standardHeaders)) {
      if (!headers.has(key)) {
        headers.set(key, value);
      }
    }

    if (!headers.has("Accept")) {
      headers.set("Accept", "application/vnd.oilfieldtracking.v5+json, application/json");
    }

    return headers;
  },
});

export const otsBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
  async (args, api, extraOptions) => {
    const baseUrl = (api.extra as OtsAsyncThunkConfigExtra<OtsState>).baseUrl;

    const urlEnd = typeof args === 'string' ? args : args.url;
    // construct a dynamically generated portion of the url
    const adjustedUrl = `${baseUrl}${urlEnd}`;
    const adjustedArgs =
      typeof args === 'string' ? adjustedUrl : {...args, url: adjustedUrl};

    // provide the amended url and other params to the raw base query
    return otsFetchBaseQuery(adjustedArgs, api, extraOptions);
  };

export const otsBaseApi = createApi({
  baseQuery: otsBaseQuery,
  endpoints: () => ({}),
})

export const getBaseUrl = (api: BaseQueryApi): string => {
  return (api.extra as OtsAsyncThunkConfigExtra<OtsState>).baseUrl
}

export const buildUrl = (api: BaseQueryApi, path: string): string => {
  const baseUrl = getBaseUrl(api)
  return `${baseUrl}${path}`
}
